import type { ComponentProps, ReactNode } from 'react';
import clsx from 'clsx';
import { Icon, InlineStack, PlainButton, Tooltip } from '@customerio/pluma-components/react';
import * as styles from './edit-trigger.css';

/**
 * What an editable cell looks like when nobody is editing it.
 *
 * Text, not a button's chrome: the page this replaces renders the value at
 * `xs` with the row's own weight and keeps the pencil at `opacity: 0` until
 * the cell is pointed at (`data-index-list-table/name-cell/index.gts:228-250`
 * and its stylesheet). A tertiary button instead reads as bold, sits on its
 * own padding — which pushed the value out of line with the name above it —
 * and stands a permanent pencil beside every row.
 *
 * Still a button when it cannot be used, so the cell does not change shape
 * depending on who is looking at it. Pluma refuses it with `aria-disabled`
 * rather than the native attribute, so it keeps its place in the tab order
 * and the tooltip explaining why can be read without a mouse — which is the
 * only thing that tooltip is for.
 *
 * The label is the caller's, because only the caller knows what it is: a
 * description truncates and mutes itself when empty, a tags cell holds
 * badges. Whatever it renders carries `styles.triggerLabel`, which is what
 * picks up the accent on hover.
 */

export type EditTriggerProps = {
  /** False for a reserved row, or for a viewer without the permission. */
  canEdit: boolean;
  /** Whether the viewer would be allowed if the row were not reserved. */
  hasPermission: boolean;
  /** Why a viewer without the permission cannot use it. */
  tooltip: string;
  onStart: () => void;
  /** The cell's own chrome, where it has any: the tags cell keeps a button's. */
  className?: string;
  children: ReactNode;
};

export function EditTrigger({ canEdit, hasPermission, tooltip, onStart, className, children }: EditTriggerProps) {
  const trigger = (extraProps: Partial<ComponentProps<typeof PlainButton>> = {}) => (
    <PlainButton
      className={clsx(styles.trigger, className)}
      // Bounded, so there is something to truncate against: a long
      // description would otherwise stretch its column.
      maxW="full"
      isDisabled={!canEdit}
      onClick={onStart}
      {...extraProps}
    >
      <InlineStack alignBlock="center" gap="025" maxW="full">
        {children}
        {canEdit ? <Icon className={styles.triggerIcon} name="edit" size="sm" color="accent" flexShrink="0" /> : null}
      </InlineStack>
    </PlainButton>
  );

  // A reserved row is nobody's to edit, so there is nothing to explain; only
  // a viewer who would otherwise be allowed is told why.
  if (canEdit || hasPermission) {
    return trigger();
  }

  // The tooltip attaches to the button rather than wrapping it: a default
  // Tooltip renders a button of its own, which cannot nest inside one.
  return (
    <Tooltip isCustom={true} content={tooltip}>
      {(context) => trigger({ ref: context.reference.setReference, ...context.reference.getReferenceProps() })}
    </Tooltip>
  );
}
